import { ChangeEvent, useCallback, useMemo, useState } from "react";
import { mergeClasses } from "@fluentui/react-components";
import {
  ArrowSyncRegular,
  ArrowUploadRegular,
  ChevronRightRegular,
} from "@fluentui/react-icons";
import { VaultFolderRow } from "apps/project-iq/components/VaultFolderRow/VaultFolderRow";
import { VAULT_SORT_CHOICES } from "apps/project-iq/pages/VaultPage/VaultPage.constants";
import { VaultSortOption } from "apps/project-iq/pages/VaultPage/VaultPage.types";
import {
  collectFolderIds,
  countVaultItems,
  pluralize,
  sortVaultNodes,
} from "apps/project-iq/pages/VaultPage/VaultPage.utils";
import { useVaultFolderListStyles } from "./VaultFolderList.styles";
import { IVaultFolderListProps } from "./VaultFolderList.types";

const TITLE_ID = "vault-folders-heading";

export const VaultFolderList = ({
  vaultName,
  nodes,
}: IVaultFolderListProps): JSX.Element => {
  const styles = useVaultFolderListStyles();
  const [expandedFolderIds, setExpandedFolderIds] = useState<
    ReadonlySet<string>
  >(new Set());
  const [sortOption, setSortOption] = useState<VaultSortOption>("foldersFirst");

  const sortedNodes = useMemo(
    () => sortVaultNodes(nodes, sortOption),
    [nodes, sortOption],
  );
  const counts = useMemo(() => countVaultItems(nodes), [nodes]);
  const allFolderIds = useMemo(() => collectFolderIds(nodes), [nodes]);
  const areAllExpanded =
    allFolderIds.length > 0 &&
    allFolderIds.every((folderId) => expandedFolderIds.has(folderId));

  const handleToggleFolder = useCallback((folderId: string) => {
    setExpandedFolderIds((current) => {
      const next = new Set(current);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }, []);

  const handleToggleAll = (): void => {
    setExpandedFolderIds(areAllExpanded ? new Set() : new Set(allFolderIds));
  };

  const handleSortChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    setSortOption(event.target.value as VaultSortOption);
  };

  return (
    <section className={styles.card} aria-labelledby={TITLE_ID}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h2 id={TITLE_ID} className={styles.title}>
            {vaultName} Vault folders
          </h2>
          <div className={styles.toolbar}>
            <button
              type="button"
              className={mergeClasses(
                styles.toolbarButton,
                styles.expandAllButton,
              )}
              onClick={handleToggleAll}
            >
              <ChevronRightRegular
                aria-hidden="true"
                className={mergeClasses(
                  styles.expandAllIcon,
                  areAllExpanded && styles.expandAllIconOpen,
                )}
              />
              {areAllExpanded ? "Collapse all" : "Expand all"}
            </button>
            {/* TODO: wire Upload to the Vault API. No behavior yet. */}
            <button type="button" className={styles.toolbarButton}>
              <ArrowUploadRegular aria-hidden="true" />
              Upload
            </button>
            {/* TODO: wire Refresh to the Vault API. No behavior yet. */}
            <button type="button" className={styles.toolbarButton}>
              <ArrowSyncRegular aria-hidden="true" />
              Refresh
            </button>
            <select
              className={styles.sortSelect}
              aria-label="Sort folders and files"
              value={sortOption}
              onChange={handleSortChange}
            >
              {VAULT_SORT_CHOICES.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p className={styles.subtitle}>Folders and files available to you</p>
        <p className={styles.counts}>
          {counts.folders + counts.files} items ·{" "}
          {pluralize(counts.folders, "folder")},{" "}
          {pluralize(counts.files, "file")}
        </p>
      </div>
      <ul className={styles.list}>
        {sortedNodes.map((node) => (
          <VaultFolderRow
            key={node.id}
            node={node}
            expandedFolderIds={expandedFolderIds}
            onToggleFolder={handleToggleFolder}
          />
        ))}
      </ul>
    </section>
  );
};
